import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { UseOutletContextTreasury } from './Treasury';
import { StateLanguage } from '../../../languages/config/StateLanguage';
import { TreasuryAccountDA } from '../../../services/admin/treasury.account.service';
import { Error } from '../../../models/error.model';
import { GlobalSwal } from '../../../styles/swal.style';
import { Message } from '../../../models/message.model';
import { legendInvalid, legendIconInvalid } from '../../../utils/legend.data.util';
import { getError, getMessage } from '../../../languages/translations/response';
import { errorWebHandler } from '../../../scripts/error.web.handler.script';

import AppFormTreasuryAccount from './component/FormTreasuryAccount';
import AppPreloader from '../../../components/Preloader';

export interface TreasuryCreatePageProps {};

let errorResponse: Error, messageResponse: Message;

const TreasuryCreatePage: React.FunctionComponent<TreasuryCreatePageProps> = props => {
  const {setRoute, companyForUser, treasuryAccountsForCompany, accountingCashAccountsForCompany, accountingPostalAccountsForCompany, accountingBankAccountsForCompany, loadAccountingAvailableAccountsForCompany, loadTreasuryAccountsForCompany} = UseOutletContextTreasury()
  const {lang} = StateLanguage()
  const navigate = useNavigate()

  const [mounted, setMounted] = useState(false)
  const [loadIndicator, setLoadIndicator] = useState('off')
  const [typeTreasuryAccount, setTypeTreasuryAccount] = useState({value: '', valid: false})
  const [accountingAccount, setAccountingAccount] = useState({value: '', valid: false, object: null as any})
  const [nameAccount, setNameAccount] = useState({value: '', valid: false})
  const [iban, setIban] = useState({value: '', valid: true})
  const [bban, setBban] = useState({value: '', valid: true})
  const [bic, setBic] = useState({value: '', valid: true})
  const [membership, setMembership] = useState({value: '', valid: true})
  const [qrIban, setQrIban] = useState({value: '', valid: true})
  const [reference, setReference] = useState({value: '', valid: true})
  const [nameHolder, setNameHolder] = useState({value: '', valid: true})
  const [street, setStreet] = useState({value: '', valid: false})
  const [streetNumber, setStreetNumber] = useState({value: '', valid: false})
  const [additional, setAdditional] = useState({value: '', valid: true})
  const [administrativeArea, setAdministrativeArea] = useState({value: '', valid: false})
  const [city, setCity] = useState({value: '', valid: false})
  const [zipCode, setZipCode] = useState({value: '', valid: false})
  const [country, setCountry] = useState({value: '', valid: false})
  const [options, setOptions] = useState({is_default: false, is_reference: false, is_holder: false})

  const submitTreasuryAccountCreate = async () => {
    setLoadIndicator('on')

    if (companyForUser && treasuryAccountsForCompany && accountingCashAccountsForCompany && accountingPostalAccountsForCompany && accountingBankAccountsForCompany && typeTreasuryAccount.valid && accountingAccount.valid && nameAccount.valid && iban.valid && bban.valid && bic.valid && membership.valid && qrIban.valid && reference.valid && reference.valid && nameHolder.valid && street.valid && streetNumber.valid && additional.valid && administrativeArea.valid && city.valid && zipCode.valid && country.valid) {
      let valueIban = iban.value.toUpperCase().trim().replaceAll(' ', '')
      let valueBban = bban.value.toUpperCase().trim().replaceAll(' ', '')
      let valueBic = bic.value.toUpperCase().trim().replaceAll(' ', '')
      let valueQrIban = qrIban.value.toUpperCase().trim().replaceAll(' ', '')
      let valueDefault = (treasuryAccountsForCompany.length === 0) ? true : options.is_default
      let addressStreetNumber = (streetNumber.value === 'NN' || streetNumber.value === 'nn') ? streetNumber.value.toUpperCase() : streetNumber.value
      let accountHolder = {full_name: nameHolder.value, address: {street: street.value, number: addressStreetNumber, additional: additional.value, administrative_area: administrativeArea.value, city: city.value, zip_code: zipCode.value, country: country.value}}

      await TreasuryAccountDA.postTreasuryAccount(companyForUser.id, typeTreasuryAccount.value, accountingAccount.value, nameAccount.value, valueIban, valueBban, valueBic, membership.value, valueQrIban, reference.value, options.is_reference, valueDefault, accountHolder).then( (response) => {
        if (response.status === 201) {
          messageResponse = response.data

          loadTreasuryAccountsForCompany(companyForUser.id)
          loadAccountingAvailableAccountsForCompany(companyForUser.id)

          GlobalSwal.fire({
            title: getMessage(messageResponse.message, lang.code),
            text: lang.labels.actionCompletedReturningToPage,
            icon: 'success',
            showConfirmButton: false,
            timer: 2200
          }).then( () => {
            navigate('/main/treasury/list', {replace: true})
          })
        } else {
          errorResponse = response.data

          GlobalSwal.fire({
            title: getError(errorResponse.code, lang.code, errorResponse.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator('off')
      })
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        if (!typeTreasuryAccount.value) legendInvalid('legend-type-account-required')
        if (!typeTreasuryAccount.value) legendInvalid('legend-accounting-account-required')
        if (!nameAccount.value) legendInvalid('legend-name-account-required')
        if (!nameHolder.value) legendIconInvalid('input-name-holder', 'legend-name-holder-required')
        if (!street.value) legendIconInvalid('input-street', 'legend-street-required')
        if (!streetNumber.value) legendIconInvalid('input-street-number', 'legend-street-number-required')
        if (!administrativeArea.value) legendIconInvalid('input-administrative-area', 'legend-administrative-area-required')
        if (!city.value) legendIconInvalid('input-city', 'legend-city-required')
        if (!zipCode.value) legendIconInvalid('input-zip-code', 'legend-zip-code-required')
        if (!country.value) legendInvalid('legend-country-required')
        setLoadIndicator('off')
      })
    }
  }

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.treasuryAccounts, branch: lang.labels.createTreasuryAccount}, company: true})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { companyForUser && treasuryAccountsForCompany && accountingCashAccountsForCompany && accountingPostalAccountsForCompany && accountingBankAccountsForCompany
        ?
        <div className="card">
          <div className="card-header">
            <div className="card-title">
              <h2>{lang.labels.createNewTreasuryAccount}</h2>
            </div>
          </div>
          <div className="card-body">
            <AppFormTreasuryAccount typeTreasuryAccount={typeTreasuryAccount} accountingAccount={accountingAccount} nameAccount={nameAccount} iban={iban} bban={bban} bic={bic} membership={membership} qrIban={qrIban} reference={reference} nameHolder={nameHolder} street={street} streetNumber={streetNumber} additional={additional} administrativeArea={administrativeArea} city={city} zipCode={zipCode} country={country} options={options} setTypeTreasuryAccount={setTypeTreasuryAccount} setAccountingAccount={setAccountingAccount} setNameAccount={setNameAccount} setIban={setIban} setBban={setBban} setBic={setBic} setMembership={setMembership} setQrIban={setQrIban} setReference={setReference} setNameHolder={setNameHolder} setStreet={setStreet} setStreetNumber={setStreetNumber} setAdditional={setAdditional} setAdministrativeArea={setAdministrativeArea} setCity={setCity} setZipCode={setZipCode} setCountry={setCountry} setOptions={setOptions}></AppFormTreasuryAccount>
          </div>
          <div className="card-footer d-flex justify-content-end">
            <button className="btn btn-primary" type="button" data-kt-indicator={loadIndicator} onClick={submitTreasuryAccountCreate}>
              <span className="indicator-label">{lang.labels.createTreasuryAccount}</span>
              <span className="indicator-progress">
                {lang.labels.pleaseWait}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            </button>
          </div>
        </div>
        :
        <AppPreloader></AppPreloader>
      }
    </div>
  )
}

export default TreasuryCreatePage;
